import { useId, useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/shared/auth';
import { Card } from '@/shared/components/ui/Card';
import { useI18n } from '@/shared/i18n/useT';
import { useOrg } from '@/shared/org';

/**
 * Create a second organization.
 *
 * The schema has allowed this since day one — `organizations_insert` plus the
 * `organizations_grant_founder` trigger — and nothing in the interface used it,
 * so anyone who wanted their personal work kept apart from their employer's had
 * nowhere to go. This is that door.
 *
 * The new organization gets its own free subscription from
 * `organizations_grant_free_subscription`, which is the whole point of billing
 * per tenant: an employee's own organization is not on their employer's plan.
 */
export function CreateOrganizationCard() {
  const { t } = useI18n();
  const org = useOrg();
  const session = useSession();
  const nameId = useId();

  const [name, setName] = useState('');
  const [state, setState] = useState<'idle' | 'saving' | 'created' | 'error'>('idle');

  const userId = session.session?.user.id ?? null;
  const canSubmit = name.trim().length > 0 && state !== 'saving' && userId !== null && !(org.status === 'ready' && org.isViewingAs);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit || !userId || (org.status === 'ready' && org.isViewingAs)) return;

    setState('saving');
    const { data, error } = await supabase
      .from('organizations')
      // `created_by` is explicit because the INSERT policy checks it against
      // `auth.uid()`; it is authorship AND, here, the founder trigger's input.
      .insert({ name: name.trim(), slug: slugFor(name), kind: 'company', created_by: userId })
      .select('id')
      .single();

    if (error || !data) {
      setState('error');
      return;
    }

    setName('');
    setState('created');
    if (org.status === 'ready') {
      // Switch first, then refresh the list: the provider keeps a still-valid
      // selection across a reload, so the user lands inside what they made.
      org.setActiveOrg(data.id);
      org.reload();
    }
  }

  return (
    <Card title={t('org.create.title')} description={t('org.create.description')}>
      <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <label htmlFor={nameId} className="mb-1 block text-sm text-muted">
            {t('org.create.nameLabel')}
          </label>
          <input
            id={nameId}
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={120}
            className="tap-target w-full rounded-full border border-border-strong bg-surface px-3 text-sm text-fg"
          />
        </div>
        <button
          type="submit"
          disabled={!canSubmit}
          className="tap-target rounded-full bg-accent px-4 text-sm font-medium text-accent-fg disabled:opacity-50"
        >
          {state === 'saving' ? t('org.create.pending') : t('org.create.submit')}
        </button>
      </form>
      {org.status === 'ready' && org.isViewingAs && <p className="mt-3 text-sm text-muted">{t('topbar.viewAs.readOnly')}</p>}

      {state === 'created' && <p className="mt-3 text-sm text-muted">{t('org.create.created')}</p>}
      {state === 'error' && (
        <p role="alert" className="mt-3 text-sm text-danger">
          {t('org.create.error')}
        </p>
      )}
    </Card>
  );
}

/**
 * A slug the check constraint accepts, from a name in any of three languages.
 *
 * The random tail is not decoration: `slug` is globally unique, two people are
 * going to call their organization "Estudio", and failing an insert on a name
 * collision would be an unexplainable error rather than a rule anyone agreed
 * to. Slugs are not shown anywhere yet, so they only have to be valid.
 */
function slugFor(name: string): string {
  const base = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);

  const tail = Math.random().toString(36).slice(2, 8);
  return base ? `${base}-${tail}` : `org-${tail}`;
}
